import React, { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, message } from 'antd';

import { deleteQuickInferenceSession } from '../../../helpers/api/quick-inference/quick-inference';
import {
  QuickInferenceWorkspace,
  type QuickInferenceWorkspaceHandle,
} from './QuickInferenceWorkspace';
import { QuickInferenceWizardHeader } from './QuickInferenceWizardHeader';
import { useQuickInferenceSessions } from './useQuickInferenceSessions';
import { useQuickInferenceUpload } from './useQuickInferenceUpload';

function QuickInferencePage() {
  const navigate = useNavigate();
  const workspaceRef = useRef<QuickInferenceWorkspaceHandle>(null);

  const sessions = useQuickInferenceSessions();

  const handleUploadSuccess = useCallback(
    (sessionId: string) => {
      navigate(`/playground/${sessionId}`);
    },
    [navigate]
  );

  const upload = useQuickInferenceUpload(handleUploadSuccess);

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await deleteQuickInferenceSession(sessionId);
        sessions.removeSession(sessionId);
        await sessions.refresh();
      } catch (err: any) {
        message.error(err?.message || 'Failed to delete');
      }
    },
    [sessions]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 bg-slate-50 p-3 dark:bg-gray-900 sm:gap-4 sm:p-4 lg:gap-6 lg:p-6">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <QuickInferenceWizardHeader
          onRefresh={sessions.refresh}
          onReset={upload.resetSelection}
          onRunInference={upload.handleSubmit}
          listLoading={sessions.loading}
          submitting={upload.submitting}
          parsingDicom={upload.parsingDicom}
          fileCount={upload.selectedFiles.length}
          files={upload.selectedFiles}
          sessionCount={sessions.total}
        />

        {sessions.error ? (
          <div className="border-t border-slate-100 px-3 py-3 dark:border-gray-700 sm:px-5">
            <Alert type="error" showIcon message={sessions.error} />
          </div>
        ) : null}

        <QuickInferenceWorkspace
          ref={workspaceRef}
          {...upload}
          sessions={sessions.items}
          sessionsTotal={sessions.total}
          sessionsStatusCounts={sessions.statusCounts}
          sessionsLoading={sessions.loading}
          sessionsLoadingMore={sessions.loadingMore}
          sessionsHasMore={sessions.hasMore}
          sessionsFilter={sessions.filter}
          sessionsSearch={sessions.search}
          onSessionsFilterChange={sessions.setFilter}
          onSessionsSearchChange={sessions.setSearch}
          onSessionsLoadMore={sessions.loadMore}
          onOpenSession={(id) => navigate(`/playground/${id}`)}
          onDeleteSession={handleDeleteSession}
        />
      </div>
    </div>
  );
}

export default QuickInferencePage;
